/**
 * Dollar Reconciliation — legacy v1 vs Step 9 for one closed month, line by line.
 * Acceptance is line-level explainability, never aggregate equality.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, CircleDashed, Lock, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import {
  Card, FinanceOnly, PageHeader, StatusPill, TenantSelect, btnGhost, btnPrimary, btnSmall,
  errorText, inputCls, money, useLatestRequest, useTenantScope,
} from '../components/finance/financeUi';

const CLASSES = ['MATCH', 'EXPECTED_DIFFERENCE', 'LEGACY_BUG', 'STEP9_BUG', 'DATA_QUALITY', 'REQUIRES_REVIEW'];
const REASONS = {
  historical_attribution_transfer: 'Historical attribution transfer', wrong_legacy_tier: 'Wrong legacy tier',
  assigned_schedule_changed: 'Assigned schedule changed', native_schedule_difference: 'Native schedule difference',
  affiliate_sponsor_correction: 'Affiliate sponsor correction', legacy_rounding: 'Legacy rounding',
  duplicate_legacy_record: 'Duplicate legacy record', missing_mid: 'Missing MID',
  bad_source_data: 'Bad source data', step9_defect: 'Actual Step 9 defect',
};
const lastClosed = () => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1); return d.toISOString().slice(0, 7); };

const Stat = ({ label, value, tone = '', testid }) => (
  <div className="rounded-xl border bg-card p-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const Check = ({ ok, label, detail, testid }) => (
  <li className="flex items-start gap-2 text-sm" data-testid={testid}>
    {ok === true ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
      : ok === false ? <XCircle className="mt-0.5 h-4 w-4 text-rose-600" />
        : <CircleDashed className="mt-0.5 h-4 w-4 text-muted-foreground" />}
    <span>{label}{detail ? <span className="block text-xs text-muted-foreground">{detail}</span> : null}</span>
  </li>
);

function LineEditor({ row, onSave, onCancel }) {
  const [form, setForm] = useState({ classification: row.classification, reason_code: row.reason_code || '', note: '' });
  return (
    <tr className="border-t bg-muted/30" data-testid="line-editor">
      <td colSpan={7} className="px-5 py-3">
        <div className="grid gap-2 sm:grid-cols-[12rem_14rem_1fr_auto]">
          <select className={inputCls} value={form.classification} data-testid="line-classification-select"
                  onChange={(e) => setForm({ ...form, classification: e.target.value,
                                             reason_code: e.target.value === 'STEP9_BUG' ? 'step9_defect' : form.reason_code })}>
            {CLASSES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
          <select className={inputCls} value={form.reason_code} data-testid="line-reason-select"
                  onChange={(e) => setForm({ ...form, reason_code: e.target.value })}>
            <option value="">Why? (reason)</option>
            {Object.entries(REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className={inputCls} placeholder="Note" value={form.note} data-testid="line-note-input"
                 onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} onClick={() => onSave(form)} data-testid="line-save-btn">Save</button>
            <button type="button" className={btnGhost} onClick={onCancel} data-testid="line-cancel-btn">Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function Lines({ report, onClassify }) {
  const [editing, setEditing] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const rows = (report.rows || []).filter((r) => !onlyOpen || !r.explained);
  return (
    <Card testid="reconciliation-lines" title="Line-level reconciliation"
          aside={(
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} data-testid="only-unexplained-toggle" />
              Unexplained only
            </label>
          )}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{['Merchant / payee', 'MID', 'Legacy', 'Step 9', 'Variance', 'Classification', ''].map((h) => (
              <th key={h} className="px-5 py-2.5 font-medium">{h}</th>))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.user_id}:${r.mid}`;
              return (
                <React.Fragment key={key}>
                  <tr className={`border-t ${!r.explained ? 'bg-amber-500/5' : ''}`} data-testid={`recon-line-${r.mid || 'none'}-${r.user_id.slice(-6)}`}>
                    <td className="px-5 py-2.5">
                      <p className="font-medium">{r.merchant_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{r.payee_name} · {r.role || '—'}</p>
                    </td>
                    <td className="px-5 py-2.5 font-mono text-xs">{r.mid || '—'}</td>
                    <td className="px-5 py-2.5 tabular-nums">{money(r.legacy_amount)}</td>
                    <td className="px-5 py-2.5 tabular-nums">{money(r.step9_amount)}</td>
                    <td className={`px-5 py-2.5 tabular-nums ${!r.variance ? '' : !r.explained ? 'text-amber-700 font-medium' : 'text-muted-foreground'}`}>
                      {r.variance ? `${r.variance > 0 ? '+' : ''}${money(r.variance)}` : '$0'}
                    </td>
                    <td className="px-5 py-2.5">
                      <StatusPill value={r.classification} />
                      <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">
                        {r.reason_label || r.reason}{r.reviewed ? ' · reviewed' : r.classification !== 'MATCH' ? ' · suggested' : ''}
                      </p>
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <button type="button" className={btnSmall} onClick={() => setEditing(editing === key ? '' : key)}
                              data-testid={`classify-btn-${r.mid || 'none'}-${r.user_id.slice(-6)}`}>Classify</button>
                    </td>
                  </tr>
                  {editing === key && (
                    <LineEditor row={r} onCancel={() => setEditing('')}
                                onSave={async (form) => { if (await onClassify(r, form)) setEditing(''); }} />
                  )}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground" data-testid="recon-no-lines">No lines.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Gate({ scope, refreshKey }) {
  const [gate, setGate] = useState(null);
  useEffect(() => {
    if (!scope.ready) return undefined;
    let alive = true;
    api.request(`/financials/step9-gate?${scope.query}`)
      .then((g) => alive && setGate(g)).catch(() => alive && setGate(null));
    return () => { alive = false; };
  }, [scope.ready, scope.query, refreshKey]);
  return (
    <Card testid="step9-gate-card" title="Step 9 cutover gate"
          aside={<StatusPill value={gate?.ready ? 'READY' : 'NOT_READY'} testid="step9-gate-status" />}>
      <ul className="space-y-2.5 px-5 py-4">
        {(gate?.checks || []).map((c) => <Check key={c.key} {...c} testid={`gate-${c.key}`} />)}
      </ul>
      <p className="flex items-start gap-2 border-t px-5 py-3 text-xs text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        v1 residual pages stay production truth until every check passes and 9.Z is accepted.
      </p>
    </Card>
  );
}

function UploadFallback({ period, scope, onDone }) {
  const send = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('period', period);
    if (scope.body.tenant_id) fd.append('tenant_id', scope.body.tenant_id);
    try {
      const res = await fetch(`${api.baseURL}/financials/reconciliation/upload`, {
        method: 'POST', headers: { Authorization: api.getHeaders().Authorization }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.detail);
      toast.success(`${data.rows} legacy row(s) loaded`);
      onDone();
    } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <label className={`${btnGhost} cursor-pointer`} data-testid="legacy-upload-btn">
      <Upload className="h-4 w-4" />Upload legacy export (CSV: email or user_id, mid, amount)
      <input type="file" accept=".csv" className="hidden" onChange={(e) => send(e.target.files?.[0])} />
    </label>
  );
}

export default function ReconciliationPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const [period, setPeriod] = useState(lastClosed());
  const [materiality, setMateriality] = useState(1);
  const [periods, setPeriods] = useState([]);
  const [report, setReport] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const nextRequest = useLatestRequest();
  const load = useCallback(async () => {
    if (!scope.ready) return;
    const isCurrent = nextRequest();
    try {
      const next = await api.request(`/financials/reconciliation?period=${period}&materiality=${materiality}&${scope.query}`);
      const known = await api.request(`/financials/reconciliation/periods?${scope.query}`);
      if (isCurrent()) { setReport(next); setPeriods(known); }
    } catch (err) { if (isCurrent()) { toast.error(errorText(err)); setReport(null); } }
  }, [period, materiality, scope.ready, scope.query, nextRequest]);
  useEffect(() => { load(); }, [load]);
  if (!['super_admin', 'iso'].includes(user?.role)) return <FinanceOnly testid="reconciliation-denied" />;

  const classify = async (row, form) => {
    try {
      await api.request('/financials/reconciliation/classify', { method: 'POST', body: JSON.stringify({
        ...scope.body, period, user_id: row.user_id, mid: row.mid, ...form, reason_code: form.reason_code || null }) });
      toast.success('Line classified');
      await load();
      return true;
    } catch (err) { toast.error(errorText(err)); return false; }
  };
  const accept = async () => {
    setConfirm(false);
    try {
      await api.request('/financials/reconciliation/accept', { method: 'POST',
        body: JSON.stringify({ ...scope.body, period, materiality: Number(materiality) }) });
      toast.success(`Reconciliation for ${period} accepted`);
      load();
    } catch (err) { toast.error(errorText(err)); }
  };
  const s = report?.summary;
  const acceptance = report?.acceptance;
  return (
    <div className="space-y-8" data-testid="reconciliation-page">
      <PageHeader title="Dollar Reconciliation"
                  subtitle="Pick any closed residual month. Legacy v1 truth is compared with Step 9 for every MID and payee — every difference must say why.">
        <TenantSelect scope={scope} testid="reconciliation-tenant-select" />
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Closed period</span>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                 className="h-10 rounded-md border bg-background px-3 text-sm" data-testid="reconciliation-period-input" list="recon-periods" />
          <datalist id="recon-periods">{periods.map((p) => <option key={p.period} value={p.period} />)}</datalist>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs text-muted-foreground">Materiality ($)</span>
          <input type="number" min="0" step="0.5" value={materiality} onChange={(e) => setMateriality(e.target.value)}
                 className="h-10 w-28 rounded-md border bg-background px-3 text-sm" data-testid="reconciliation-materiality-input" />
        </label>
      </PageHeader>

      {report && report.status !== 'READY' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="reconciliation-unavailable">
          <div>
            <StatusPill value={report.status} testid="reconciliation-status" />
            <p className="mt-2 text-sm">Reason: {report.reason}</p>
          </div>
          {report.status === 'RECONCILIATION_NOT_AVAILABLE' && <UploadFallback period={period} scope={scope} onDone={load} />}
        </div>
      )}

      {s && (
        <>
          <section className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6" data-testid="reconciliation-summary">
            <Stat label="Legacy total" value={money(s.legacy_total)} testid="recon-legacy-total" />
            <Stat label="Step 9 total" value={money(s.step9_total)} testid="recon-step9-total" />
            <Stat label="Net variance" value={`${money(s.net_variance)}${s.variance_pct != null ? ` · ${s.variance_pct}%` : ''}`} testid="recon-net-variance" />
            <Stat label="Gross line variance" value={money(s.gross_absolute_variance)} tone="text-amber-700" testid="recon-gross-variance" />
            <Stat label="Matched dollars" value={money(s.matched_dollars)} tone="text-emerald-700" testid="recon-matched" />
            <Stat label="Unresolved" value={money(s.unresolved)} tone={s.unresolved ? 'text-rose-600' : 'text-emerald-700'} testid="recon-unresolved" />
            <Stat label="Expected differences" value={money(s.expected_differences)} testid="recon-expected" />
            <Stat label="Legacy bugs" value={money(s.legacy_bugs)} testid="recon-legacy-bugs" />
            <Stat label="Step 9 bugs" value={money(s.step9_bugs)} tone={s.step9_bugs ? 'text-rose-600' : ''} testid="recon-step9-bugs" />
            <Stat label="Data quality" value={money(s.data_quality)} testid="recon-data-quality" />
            <Stat label="Lines" value={s.lines} testid="recon-lines-count" />
            <Stat label="Legacy source" value={(report.legacy_source || '').replace(/_/g, ' ')} testid="recon-legacy-source" />
          </section>
          <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
            <Lines report={report} onClassify={classify} />
            <div className="space-y-6">
              <Card testid="reconciliation-acceptance" title="Acceptance"
                    aside={acceptance?.accepted ? <StatusPill value="ACCEPTED" testid="recon-accepted-pill" /> : null}>
                <ul className="space-y-2.5 px-5 py-4">
                  {(acceptance?.checks || []).map((c) => <Check key={c.key} ok={c.ok} label={c.label} detail={`${c.count}`} testid={`accept-check-${c.key}`} />)}
                </ul>
                <div className="space-y-2 border-t px-5 py-4">
                  {confirm ? (
                    <div className="space-y-2" data-testid="accept-confirm-panel">
                      <p className="text-sm font-medium">Accept {period} as reconciled? A snapshot is recorded.</p>
                      <div className="flex gap-2">
                        <button type="button" className={btnPrimary} onClick={accept} data-testid="accept-confirm-btn">Yes, accept</button>
                        <button type="button" className={btnGhost} onClick={() => setConfirm(false)} data-testid="accept-cancel-btn">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={`${btnPrimary} w-full`} disabled={!acceptance?.ready || Boolean(acceptance?.accepted)}
                            onClick={() => setConfirm(true)} data-testid="accept-reconciliation-btn">
                      {acceptance?.accepted ? 'Accepted' : 'Accept reconciliation'}
                    </button>
                  )}
                  {acceptance?.accepted && (
                    <p className="text-xs text-muted-foreground">Last accepted {new Date(acceptance.accepted.accepted_at).toLocaleString()}</p>
                  )}
                </div>
              </Card>
              <Gate scope={scope} refreshKey={report} />
            </div>
          </div>
        </>
      )}
      {!s && <Gate scope={scope} refreshKey={report} />}
    </div>
  );
}
